import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { resolveAuditReferences } from "@/lib/references";
import { PageHeader } from "@/components/base/PageHeader";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { fmtDateTime } from "@/lib/format";

interface AuditRow {
  id: string;
  table_name: string;
  record_id: string;
  action: string;
  changes: Record<string, unknown> | null;
  reason: string | null;
  performed_by: string | null;
  performed_at: string;
}

const actionColor: Record<string, string> = {
  insert: "bg-emerald-50 text-emerald-700",
  create: "bg-emerald-50 text-emerald-700",
  received: "bg-emerald-50 text-emerald-700",
  update: "bg-slate-100 text-slate-700",
  delete: "bg-red-50 text-red-700",
  cancelled: "bg-red-50 text-red-700",
};

function summarizeChanges(changes: Record<string, unknown> | null): string {
  if (!changes || typeof changes !== "object") return "";
  const entries = Object.entries(changes).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return "";
  return entries
    .slice(0, 4)
    .map(([k, v]) => {
      const val = typeof v === "object" ? JSON.stringify(v) : String(v);
      return `${k}: ${val}`;
    })
    .join(" · ");
}

export default function Audit() {
  const { org } = useAuth();
  const [filter, setFilter] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["audit", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("audit_logs")
        .select("id, table_name, record_id, action, changes, reason, performed_by, performed_at")
        .eq("org_id", org!.id)
        .order("performed_at", { ascending: false })
        .limit(300);
      const rows = (data ?? []) as AuditRow[];
      const userIds = Array.from(new Set(rows.map((r) => r.performed_by).filter((id): id is string => !!id)));
      const { data: profiles } = userIds.length
        ? await supabase.from("profiles").select("id, full_name").in("id", userIds)
        : { data: [] };
      const nameById: Record<string, string> = {};
      (profiles ?? []).forEach((p) => {
        if (p.id && p.full_name) nameById[p.id] = p.full_name;
      });
      const refs = await resolveAuditReferences(org!.id, rows);
      return { rows, nameById, refs };
    },
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!filter) return data.rows;
    return data.rows.filter((l) => l.table_name === filter);
  }, [data, filter]);

  const tables = useMemo(() => {
    const set = new Set((data?.rows ?? []).map((l) => l.table_name));
    return Array.from(set).sort();
  }, [data]);

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Who changed what, when, and why" />

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <option value="">All entities</option>
            {tables.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-400">{filtered.length} entries</span>
        </div>

        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500 mb-4">Couldn't load the audit log.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No audit entries" description="Actions you take will appear here automatically." />
        ) : (
          <div className="divide-y divide-slate-50">
            {filtered.map((l) => {
              const changes = summarizeChanges(l.changes);
              const actor = l.performed_by ? data?.nameById[l.performed_by] ?? "System" : "System";
              const ref = data?.refs.get(`${l.table_name}:${l.record_id}`);
              return (
                <div key={l.id} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionColor[l.action] ?? "bg-slate-100 text-slate-700"}`}>
                      {l.action}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">
                        <span className="font-medium capitalize">{l.table_name.replace(/_/g, " ")}</span>
                        {" · "}
                        {ref?.route ? (
                          <Link to={ref.route} className="text-emerald-700 hover:underline font-medium">{ref.label}</Link>
                        ) : (
                          <span className="text-slate-600 font-medium">{ref?.label ?? l.record_id}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {actor} · {fmtDateTime(l.performed_at)}
                      </p>
                    </div>
                  </div>
                  {changes && <p className="text-xs text-slate-500 mt-2 font-mono">{changes}</p>}
                  {l.reason && <p className="text-xs text-slate-500 mt-1 italic">“{l.reason}”</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}